// Device Details 1NCE SIM actions (documentation/onence.md "SIM actions"): disable/enable, IMEI
// lock/unlock and connectivity reset of the radio's SIM, platform administrators only. The server
// decides everything: whether actions are switched on, which SIM, which Management API version, and
// whether each action is available. This view only offers what the server's panel offers (one button
// per state pair: disable or enable, IMEI lock or unlock, for the state the server reports), asks for an
// explicit confirmation that names the target and the consequences, sends the server's own view back,
// and reports the outcome honestly (a reset is "queued", never "done"; a timeout is "unknown").
import { useState } from 'react';
import { displayIccid, runOnenceSimAction, type OnenceActionPanel, type OnenceActionResult, type OnenceSimAction } from '../../services/onence';
import { actionErrorText, actionMeta, lastActionText, outcomeText, simActionControls } from './onenceSimActionText';

export interface SimActionsViewProps {
  panel: OnenceActionPanel;
  /** The SIM label 1NCE reports (for the confirmation). */
  simLabel?: string;
  radioName?: string;
  /** The action awaiting confirmation. */
  pending: OnenceSimAction | null;
  busy: boolean;
  result: OnenceActionResult | null;
  error: string | null;
  onSelect: (a: OnenceSimAction) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Pure presentation of the actions panel (tests render each state). */
export function SimActionsView({ panel, simLabel, radioName, pending, busy, result, error, onSelect, onConfirm, onCancel }: SimActionsViewProps) {
  const last = panel.last && <p className="text-xs text-gray-400" data-onence-action-last={panel.last.state}>Last SIM action: {lastActionText(panel.last)}</p>;
  if (!panel.enabled) {
    return (
      <div className="text-xs text-gray-500" data-onence-actions="disabled">
        <p>SIM actions are turned off on this server.</p>
        {last}
      </div>
    );
  }
  const exp = panel.expected;
  const controls = !panel.blocked_code && exp && panel.options.length > 0;
  const meta = pending ? actionMeta[pending] : null;
  return (
    <section className="space-y-2" aria-label="SIM actions" data-onence-actions={controls ? 'available' : 'blocked'}>
      <p className="text-xs text-gray-400 uppercase font-semibold">SIM actions</p>
      {!controls && <p className="text-xs text-yellow-200" data-onence-actions-blocked={panel.blocked_code ?? ''}>No SIM actions: {panel.blocked ?? 'not available'}</p>}
      {controls && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="SIM actions">
          {simActionControls(panel).map((c) => {
            const why = `onence-action-why-${c.key}`;
            const act = c.action;
            return (
              <span key={c.key} className="inline-flex flex-col">
                <button type="button" data-onence-action={act ?? undefined} data-onence-action-control={c.key}
                  disabled={!c.available || !act || busy || pending !== null}
                  aria-describedby={!c.available && c.reason ? why : undefined} onClick={() => { if (act) onSelect(act); }}
                  className={`text-xs px-2 py-1 rounded disabled:opacity-50 ${c.danger ? 'bg-red-900/60 hover:bg-red-800 text-red-100' : 'bg-gray-700 hover:bg-gray-600 text-gray-100'}`}>
                  {c.label}
                </button>
                {!c.available && c.reason && <span id={why} className="text-[11px] text-gray-500 max-w-[14rem]">{c.reason}</span>}
              </span>
            );
          })}
        </div>
      )}
      {controls && meta && pending && exp && (
        <div role="alertdialog" aria-modal="false" aria-labelledby="onence-action-title" aria-describedby="onence-action-effects"
          className="rounded border border-yellow-700 bg-yellow-950/40 p-3 space-y-2" data-onence-confirm={pending}>
          <p id="onence-action-title" className="text-sm font-semibold text-white">{meta.title}</p>
          <dl className="grid grid-cols-[8rem_1fr] gap-x-2 gap-y-0.5 text-xs">
            {radioName && <><dt className="text-gray-400">Radio</dt><dd className="text-gray-100">{radioName}</dd></>}
            <dt className="text-gray-400">SIM (ICCID)</dt><dd className="font-mono text-gray-100" data-onence-confirm-iccid>{displayIccid(exp.iccid)}</dd>
            {simLabel && <><dt className="text-gray-400">1NCE label</dt><dd className="text-gray-100">{simLabel}</dd></>}
            <dt className="text-gray-400">Now</dt><dd className="text-gray-100">{exp.sim_status}{exp.imei_lock === true ? ', IMEI lock on' : exp.imei_lock === false ? ', IMEI lock off' : ''}</dd>
            <dt className="text-gray-400">1NCE API</dt><dd className="text-gray-100" data-onence-confirm-version>{exp.api_version}</dd>
          </dl>
          <ul id="onence-action-effects" className="list-disc pl-5 text-xs text-gray-200 space-y-0.5">
            {meta.effects.map((e) => <li key={e}>{e}</li>)}
          </ul>
          <div className="flex gap-2">
            <button type="button" data-onence-confirm-go onClick={onConfirm} disabled={busy}
              className="text-xs px-2 py-1 rounded bg-red-800 hover:bg-red-700 text-white disabled:opacity-50">
              {busy ? 'Sending…' : `Confirm: ${meta.label}`}
            </button>
            <button type="button" data-onence-confirm-cancel onClick={onCancel} disabled={busy}
              className="text-xs px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-gray-100 disabled:opacity-50">Cancel</button>
          </div>
        </div>
      )}
      {result && <p role="status" className={`text-xs ${result.outcome === 'uncertain' || result.outcome === 'rejected' ? 'text-red-300' : 'text-green-200'}`}
        data-onence-action-result={result.outcome}>{actionMeta[result.action]?.label ?? result.action}: {outcomeText(result)}
        {!result.audit_recorded && ' The outcome could not be written to the audit log.'}</p>}
      {error && <p role="alert" className="text-xs text-red-300" data-onence-action-error>{error}</p>}
      {last}
    </section>
  );
}

/** Stateful wrapper: select -> confirm -> one request -> result -> the caller re-reads the SIM. */
export function OnenceSimActions({ radioId, panel, simLabel, radioName, onDone }: {
  radioId: string; panel: OnenceActionPanel; simLabel?: string; radioName?: string; onDone: () => void;
}) {
  const [pending, setPending] = useState<OnenceSimAction | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<OnenceActionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const confirm = async () => {
    if (!pending || !panel.expected || busy) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await runOnenceSimAction(radioId, pending, panel.expected));
    } catch (err) {
      setError(actionErrorText(err));
    } finally {
      setBusy(false);
      setPending(null);
      onDone();
    }
  };
  return (
    <SimActionsView panel={panel} simLabel={simLabel} radioName={radioName} pending={pending} busy={busy} result={result} error={error}
      onSelect={(a) => { setResult(null); setError(null); setPending(a); }} onConfirm={confirm} onCancel={() => setPending(null)} />
  );
}
